/**
 * The order book, kept live — plan-005 OP-36 (FR-PAY-11, design §18.14.3, DD5-33).
 *
 * Every change to an order — a status move at the counter, a payment confirmed by EveryPay, a
 * refund, an expired link — is published on the shop's order stream as the existing
 * `customer_order` event with empty data, meaning "re-read". So the Orders tab and the Order
 * detail do not patch anything from a payload: they invalidate the order book's queries and let
 * them refetch, exactly as the counter board does. A paid order therefore moves from "Awaiting
 * payment" to "New" on an open screen without a reload.
 *
 * `customer_order_payment` is listened to as well. The design puts it on the conversation's
 * transcript channel only (the portal test panel's payment element), never on the shop stream;
 * treating it as a re-read here costs nothing if it never comes and keeps the screens right if
 * a relay ever forwards it.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useGateway } from "../../../../api/provider.js";
import { CUSTOMER_ORDER_REREAD_EVENT } from "../../../../api/slice/customer-order-payment.js";

/** Every query the order book owns — the prefixes of `ORDER_BOOK_KEY` and the detail's key. */
export const ORDER_BOOK_QUERY_PREFIX = ["order-book-summary", "order-book-page", "order-book-detail"] as const;

/**
 * Subscribes to the shop's order stream while `shopId` is known, and re-reads the order book on
 * every change. The first `snapshot` a connection sends is what the screen already read, so it is
 * skipped; one after a reconnect is not, because whatever changed meanwhile reached nobody.
 */
export function useShopCustomerOrderStream(shopId: string | null): void {
  const gateway = useGateway();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (shopId === null) return undefined;
    let snapshotSeen = false;
    const subscription = gateway.streamShopCustomerOrder(shopId, (frame) => {
      if (!CUSTOMER_ORDER_REREAD_EVENT.includes(frame.event)) return;
      if (frame.event === "snapshot" && !snapshotSeen) {
        snapshotSeen = true;
        return;
      }
      for (const prefix of ORDER_BOOK_QUERY_PREFIX) {
        void queryClient.invalidateQueries({ queryKey: [prefix] });
      }
    });
    return () => {
      subscription.close();
    };
  }, [gateway, queryClient, shopId]);
}
